// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client.js';

export interface WebhookEndpointView {
  id: string;
  name: string;
  url: string;
  eventTypes: string[];
  formTypeFilter: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Create/rotate responses carry the signing secret exactly once. */
export interface WebhookWithSecret extends WebhookEndpointView {
  secret: string;
}

const QUERY_KEY = ['webhooks'] as const;

export function useWebhooks() {
  return useQuery<WebhookEndpointView[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await apiFetch<{ items: WebhookEndpointView[] }>('/api/webhooks');
      return res.items;
    },
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation<
    WebhookWithSecret,
    ApiError,
    { name: string; url: string; formTypeFilter?: string | null }
  >({
    mutationFn: (body) => apiFetch<WebhookWithSecret>('/api/webhooks', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation<
    WebhookEndpointView,
    ApiError,
    {
      id: string;
      patch: { enabled?: boolean; name?: string; url?: string; formTypeFilter?: string | null };
    }
  >({
    mutationFn: ({ id, patch }) =>
      apiFetch<WebhookEndpointView>(`/api/webhooks/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRotateWebhookSecret() {
  const qc = useQueryClient();
  return useMutation<WebhookWithSecret, ApiError, string>({
    mutationFn: (id) =>
      apiFetch<WebhookWithSecret>(`/api/webhooks/${id}/rotate-secret`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useTestWebhook() {
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/webhooks/${id}/test`, { method: 'POST' }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/webhooks/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
